import { FC, useMemo } from "react";
import {
  Nullable,
  useDriverControllerDelete,
  useDriverControllerUpdateDriverAppAccess,
} from "service";

import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";

import { useNavigate } from "react-router-dom";
import { generateRoute, makeSxStyles } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";
import { AdminDriverResViewDto } from "service/api/estabrookSchemas";
import {
  Check,
  Close,
  TrendingUp,
  TrendingDown,
  InfoOutlined,
} from "@mui/icons-material";
import { formatDateTime } from "../../../utils/string-helpers";
import { Box, Button, Typography, Tooltip } from "@mui/material";
import { useDialogStore } from "../../../state/dialog-store";
import { toast } from "react-toastify";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import InteractableCell from "../components/generic-table/interactable-cell";
import CheckLabel from "../customer-management/check-label";

const sanitizedHeader = [
  {
    label: "Name",
    value: "name",
    sortable: true,
    size: "10%",
  },
  {
    label: (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="caption" sx={{ lineHeight: 1.2, fontWeight: 500 }}>
          Username
        </Typography>
        <Typography
          variant="caption"
          sx={{ lineHeight: 1.2, fontSize: "0.7rem", opacity: 0.8 }}
        >
          E-mail address
        </Typography>
      </Box>
    ),
    value: "email",
    sortable: true,
    size: "10%",
  },
  {
    label: "Vehicle",
    value: "vehicle",
    sortableValue: "licensePlate",
    sortable: true,
    size: "8%",
  },
  {
    label: "Starting Point",
    value: "startingPoint",
    sortable: true,
    size: "15%",
  },
  {
    label: "Availability",
    value: "available",
    sortable: true,
    size: "8%",
  },
  {
    label: (
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{ whiteSpace: "nowrap" }}
      >
        <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
          Service Access
        </Typography>
        <Tooltip title="Click to toggle service access.">
          <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
        </Tooltip>
      </Box>
    ),
    value: "appAccess",
    sortable: true,
    size: "8%",
  },
  {
    label: "Verified",
    value: "driverCompletedRegistration",
    sortable: true,
    size: "8%",
  },
  {
    label: "Performance Time",
    value: "performanceTime",
    size: "9%",
    sortable: true,
    sortableValue: "performanceTime",
  },
  {
    label: "Last Plowed On",
    size: "7%",
    value: "lastPlow",
    sortable: true,
    sortableValue: "lastPlow",
  },
  {
    label: "Last Updated",
    size: "10%",
    value: "updatedAt",
    sortable: true,
    sortableValue: "updatedAt",
  },
  {
    label: "",
    value: "actions",
    size: "7%",
  },
];

export interface DriverManagementTableProps {
  data?: AdminDriverResViewDto[];
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
  isLoading: boolean;
}

const DriverManagementTable: FC<DriverManagementTableProps> = (props) => {
  const { data = [], sortOptions, sortOptionsChange, isLoading } = props;
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();

  const { mutate: removeDriver } = useDriverControllerDelete({
    onSuccess: () => {
      toast.success("Driver Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/drivers",
          operationId: "driverControllerGetAll",
          variables: {},
        }),
      );
      // Invalidate unassigned vehicles query since the deleted driver's vehicle is now unassigned
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles/unassigned",
          operationId: "vehicleControllerGetUnassignedVehicles",
          variables: {},
        }),
      );
    },
  });

  const { mutate: changeAppAccess, isLoading: isAppAccessLoading } =
    useDriverControllerUpdateDriverAppAccess({
      onSuccess: () => {
        toast.success("Service Access has been Updated Successfully");
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/drivers",
            operationId: "driverControllerGetAll",
            variables: {},
          }),
        );
      },
    });

  const performanceComponent = (data: number) => {
    if (data < 0) {
      return (
        <Box sx={styles.trendingDown}>
          <TrendingDown style={{ marginRight: "5px" }} />
          {Math.abs(data)} %
        </Box>
      );
    }

    if (data === 0) {
      return (
        <Box sx={styles.neutralTrend}>
          <span
            style={{
              width: "13px",
              height: "1.67px",
              display: "flex",
              backgroundColor: "#fff",
              margin: "0 12px 0 4px",
            }}
          ></span>{" "}
          {data} %
        </Box>
      );
    }
    return (
      <Box sx={styles.trendingUp}>
        <TrendingUp style={{ marginRight: "5px" }} />
        {data} %
      </Box>
    );
  };

  const handleEdit = (id: string) => {
    navigate(generateRoute(AdRoutes.editDriver, { driverId: id }));
  };

  const handleDelete = (id: string, name: string) => {
    setDialog({
      title: "Delete Driver",
      description: `Are you sure you want to delete driver ${name}? This will unassign all linked regions and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        removeDriver({ pathParams: { driverId: id || "" } });
        onClose();
      },
    });
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminDriverResViewDto>
  > = useMemo(
    () => ({
      name: ({ headerValue, data: { name } }) => ({
        label: (
          <Typography variant="body2" noWrap>
            {name}
          </Typography>
        ),
        value: headerValue,
      }),
      email: ({ headerValue, data: { email, username } }) => ({
        label: (
          <Box>
            <Typography variant="body2" noWrap>
              {email?.toLowerCase() ?? username?.toLowerCase()}
            </Typography>
          </Box>
        ),
        value: headerValue,
      }),
      vehicle: ({ headerValue, data: { vehicle } }) => ({
        label: vehicle?.licensePlate ?? "No Vehicle",
        value: headerValue,
      }),
      startingPoint: ({ headerValue, data: { startingPoint } }) => ({
        label: startingPoint?.address ?? "No Address",
        value: headerValue,
        customCSS: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "175px",
        },
      }),
      available: ({ headerValue, data: { available, vehicle } }) => ({
        label:
          available && vehicle?.licensePlate ? (
            <Check color="success" />
          ) : (
            <Close color="error" />
          ),
        value: headerValue,
      }),
      appAccess: ({ headerValue, data: { appAccess, id } }) => ({
        label: (
          <Tooltip title="Click to toggle service access.">
            <Button
              sx={{ maxWidth: "50px" }}
              onClick={() =>
                changeAppAccess({
                  pathParams: { driverId: id },
                  body: {
                    appAccess: !appAccess,
                  },
                })
              }
              disabled={isAppAccessLoading}
            >
              <CheckLabel isChecked={appAccess} />
            </Button>
          </Tooltip>
        ),
        value: headerValue,
      }),
      driverCompletedRegistration: ({
        headerValue,
        data: { driverCompletedRegistration },
      }) => ({
        label: driverCompletedRegistration ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        ),
        value: headerValue,
      }),
      performanceTime: ({ headerValue, data: { performanceTime } }) => ({
        label: performanceComponent(performanceTime),
        value: headerValue,
      }),
      createdAt: ({ headerValue, data: { createdAt } }) => ({
        label: createdAt,
        value: headerValue,
      }),
      lastPlow: ({ headerValue, data: { vehicle } }) => ({
        label: vehicle?.lastPlow ?? "No Plow",
        value: headerValue,
      }),
      updatedAt: ({ headerValue, data: { updatedAt } }) => ({
        label: formatDateTime(updatedAt, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        value: headerValue,
      }),
      actions: ({ headerValue, data }) => {
        const { id, name } = data;
        return {
          label: (
            <InteractableCell
              label={""}
              onDelete={() => handleDelete(id, name)}
              onEdit={() => handleEdit(id)}
            />
          ),
          value: headerValue,
        };
      },
    }),
    [],
  );

  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabel}
      data={data}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

const useStyles = makeSxStyles(() => ({
  trendingUp: {
    display: "flex",
    alignItems: "center",
    color: "#60DBB9",
    svg: {
      marignRight: "5px",
    },
  },
  trendingDown: {
    display: "flex",
    alignItems: "center",
    color: "#FFB4AB",
    svg: {
      marignRight: "5px",
    },
  },
  neutralTrend: {
    color: "#fff",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  hiddenButton: {
    opacity: "none",
    "&:hover": {
      display: "#block",
    },
  },
}));

export default DriverManagementTable;
